import { Button } from "@smartsuite/react-ui/lib/components/buttons/Button/Button";
import { whiteButtonStyles } from "@smartsuite/react-ui/lib/components/buttons/Button/Button.constant";
import { FieldLabel } from "@smartsuite/react-ui/lib/components/forms/FieldLabel/FieldLabel";
import { NumberInputField } from "@smartsuite/react-ui/lib/components/forms/NumberInputField/NumberInputField";
import { KeyValueCustomSelect } from "@smartsuite/react-ui/lib/components/selects/CustomSelect/CustomSelect";
import { useSelectState } from "@smartsuite/react-ui/lib/components/selects/useSelectState/useSelectState";
import { Paragraph } from "@smartsuite/react-ui/lib/components/typography/Paragraph/Paragraph";
import { Text } from "@smartsuite/react-ui/lib/components/typography/Text/Text";
import { Title } from "@smartsuite/react-ui/lib/components/typography/Title/Title";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import type { CanvasPaperConfig } from "../PageDesigner/PageDesigner";
import {
  BackgroundStyle,
  backgroundStyleOptions,
  getOrientedSize,
  PaperOrientation,
  PaperOrientationOptions,
  PaperSize,
  type PaperSizeConfig,
  paperSizeConfigs,
  paperSizeOptions,
} from "../paper.constants";
import {
  DirectionsInputs,
  type updateValues,
} from "../SidebarSelectionConfig/DirectionsInputs/DirectionsInputs";
import { StyledSwitchButton } from "../StyledSwitchButton/StyledSwitchButton";
import { showElementWithoutImplementation } from "../utils";

/** Finds the preset the given page matches, whichever way round it is laid out. */
const findPaperSize = (page: PaperSizeConfig): PaperSize | undefined => {
  const standingUp = getOrientedSize(page, PaperOrientation.Portrait);

  return (Object.keys(paperSizeConfigs) as PaperSize[]).find((paperSize) => {
    const preset = paperSizeConfigs[paperSize];

    return (
      preset.width === standingUp.width && preset.height === standingUp.height
    );
  });
};

export interface SidebarPaperConfigProps {
  canvasPaperConfig: CanvasPaperConfig;
  onCanvasPaperConfigUpdate: (
    onCanvasPaperConfigUpdate: CanvasPaperConfig,
  ) => void;
}

export const SidebarPaperConfig: React.FC<SidebarPaperConfigProps> = ({
  canvasPaperConfig,
  onCanvasPaperConfigUpdate,
}) => {
  const [paperSizeSelectState, setPaperSizeSelectState] = useSelectState();
  const [paperSize, setPaperSize] = useState(PaperSize.Letter);
  const [backgroundStyle, setBackgroundStyle] = useState(
    BackgroundStyle.FitCanvas,
  );

  const pageBreakMarginRef = useRef<HTMLInputElement>(null);
  const [pageBreakMarginSelectState, setPageBreakMarginSelectState] =
    useSelectState();

  const { orientation, paddingDirections, pageBreakMargin } = canvasPaperConfig;

  useEffect(() => {
    const matchingPaperSize = findPaperSize(canvasPaperConfig);

    if (matchingPaperSize) {
      setPaperSize(matchingPaperSize);
    }
  }, [canvasPaperConfig]);

  /**
   * The page dimensions are always re-derived from the sheet and the
   * orientation together, so the two can never drift apart on save. Only a
   * paper size change takes its sheet from a preset — turning the page keeps
   * the design's own dimensions, which may not match any preset.
   */
  const updatePaperLayout = useCallback(
    (layout: { paperSize?: PaperSize; orientation?: PaperOrientation }) => {
      const nextOrientation = layout.orientation ?? orientation;
      const sheet = layout.paperSize
        ? paperSizeConfigs[layout.paperSize]
        : canvasPaperConfig;

      onCanvasPaperConfigUpdate({
        ...canvasPaperConfig,
        ...getOrientedSize(sheet, nextOrientation),
        orientation: nextOrientation,
      });

      if (layout.paperSize) {
        setPaperSize(layout.paperSize);
      }
    },
    [canvasPaperConfig, onCanvasPaperConfigUpdate, orientation],
  );

  const updateSpacing = useCallback(
    (value: updateValues) => {
      onCanvasPaperConfigUpdate({
        ...canvasPaperConfig,
        paddingDirections: {
          ...paddingDirections,
          [value.direction]: value.value,
        },
      });
    },
    [canvasPaperConfig, onCanvasPaperConfigUpdate, paddingDirections],
  );

  const updatePageBreakMargin = useCallback(
    (value: number) => {
      onCanvasPaperConfigUpdate({
        ...canvasPaperConfig,
        pageBreakMargin: value,
      });
    },
    [canvasPaperConfig, onCanvasPaperConfigUpdate],
  );

  return (
    <div className="sidebar-paper-config">
      <div className="sidebar-paper-config__section sidebar-paper-config__section__overflow">
        <Title size="xs" weight="medium">
          Design Settings
        </Title>

        <div>
          <Text
            tag="div"
            weight="medium"
            color={paperSizeSelectState === "active" ? "link" : "primary"}
            className="sidebar-paper-config__title"
          >
            Paper Size
          </Text>

          <KeyValueCustomSelect
            state={paperSizeSelectState}
            value={paperSize}
            options={paperSizeOptions}
            placeholderArrowStyle="filled-dark"
            showClear={false}
            onChange={(paperSizeValue) =>
              updatePaperLayout({ paperSize: paperSizeValue as PaperSize })
            }
            onOpenedChange={setPaperSizeSelectState}
          />
        </div>

        <div>
          <Text
            tag="div"
            weight="medium"
            className="sidebar-paper-config__title"
          >
            Paper Orientation
          </Text>
          <StyledSwitchButton
            options={PaperOrientationOptions}
            currentValue={orientation}
            onChange={(orientationValue) =>
              updatePaperLayout({
                orientation: orientationValue as PaperOrientation,
              })
            }
          />
        </div>

        <div>
          <header className="sidebar-paper-config__title">
            <Text tag="div" weight="medium">
              Page Break Margin
            </Text>
            <Paragraph
              color="inactive"
              size="xs"
              className="sidebar-paper-config__subtitle"
            >
              Margin applied to elements before and after a page break.
            </Paragraph>
          </header>
          <NumberInputField
            ref={pageBreakMarginRef}
            onFocus={() => setPageBreakMarginSelectState(true)}
            onBlur={() => setPageBreakMarginSelectState(false)}
            state={pageBreakMarginSelectState ? "active" : undefined}
            showSymbol={false}
            autoHideNavigation={true}
            inputStyles={{
              backgroundColor: "white",
              borderColor: "G-1-5",
              borderHoverColor: "G-3",
              borderFocusColor: "G-3",
            }}
            max="999"
            min="0"
            value={`${pageBreakMargin}`}
            onChangeValue={(value) => {
              if (pageBreakMarginRef.current)
                pageBreakMarginRef.current.focus();
              updatePageBreakMargin(value ? parseInt(value) : 0);
            }}
          />
        </div>

        {showElementWithoutImplementation && (
          <>
            <div>
              <FieldLabel>Background Image</FieldLabel>
              <Button
                className="sidebar-paper-config__full sidebar-paper-config__text-center"
                styles={{
                  ...whiteButtonStyles,
                  height: 36,
                }}
              >
                Browse File
              </Button>
            </div>
            <div>
              <FieldLabel>Background Style</FieldLabel>
              <KeyValueCustomSelect
                value={backgroundStyle}
                options={backgroundStyleOptions}
                onChange={(backgroundStyleValue) =>
                  setBackgroundStyle(backgroundStyleValue as BackgroundStyle)
                }
              />
            </div>
          </>
        )}

        {showElementWithoutImplementation && (
          <DirectionsInputs
            title="Padding"
            values={paddingDirections}
            updateValues={(value) => updateSpacing(value)}
          />
        )}
      </div>
    </div>
  );
};
